import GooglePayButton from '@google-pay/button-react';
import { useTrackPageView } from '@smartpay/mixpanel';
import cx from 'classnames';
import { KeyboardEventHandler, MouseEventHandler, useState } from 'react';
import { useAppDispatch } from '../..';
import ERROR_MESSAGES from '../../api/error-messages';
import { APIPayload } from '../../api/types';
import ApplePayMark from '../../assets/apple-pay.svg';
import IconInfoGray from '../../assets/icon-info-gray.svg';
import Button from '../../components/Form/Button';
import TextInput from '../../components/Form/TextInput';
import Popup from '../../components/Popup/Popup';
import useAppSelector from '../../hooks/use-app-selector';
import usePaymentMethods from '../../hooks/use-payment-methods';
import { setHasAppliedPromotionCode } from '../../redux/good';
import { getPromotionCode } from '../../redux/misc';
import { reserveDiscount } from '../../redux/payment-method';
import { currencyFormatter } from '../../utils';
import createEventHandler from '../../utils/create-event-handler';
import { getAmountForTheFirst } from '../../utils/installments';
import validatePromotion from '../../utils/validate-promotion';
import MethodSelector from './MethodSelector';
import styles from './PayActionForm.module.scss';
import screenStyles from './PaymentScreen.module.scss';

declare global {
  interface Window {
    ApplePaySession: ApplePaySession;
  }
}

const ThirdPartyButtons = ({
  onApplePay,
  onGooglePay,
  onGooglePayCancel,
  onGooglePayClick,
  googlePaymentRequest,
}: {
  onApplePay: MouseEventHandler<HTMLButtonElement>;
  onGooglePay: (paymentData: google.payments.api.PaymentData) => void;
  onGooglePayCancel: Function;
  onGooglePayClick: Function;
  googlePaymentRequest: google.payments.api.PaymentDataRequest;
}) => {
  const isTestAccount = useAppSelector((state) => state.auth.isTestAccount);
  const isDemoSite = useAppSelector((state) => state.misc.isDemoSite);
  const [showApplePayPopup, setShowApplePayPopup] = useState(false);
  const supportsApplePay = useAppSelector(
    (state) => state.misc.supportsApplePay
  );
  const supportsGooglePay = useAppSelector(
    (state) => state.misc.supportsGooglePay
  );

  const onFakeApplePayClick = () => {
    setShowApplePayPopup(!showApplePayPopup);
  };

  return (
    <>
      <nav className={styles['third-party']}>
        {isTestAccount && (
          <div className={styles['info-wrapper']}>
            <p>
              現時点では、テストモードでの
              {window.ApplePaySession
                ? 'Apple Pay及びGoogle Pay'
                : 'Google Pay'}
              には対応していません。
            </p>
          </div>
        )}
        {supportsApplePay && !!window.ApplePaySession && (
          <>
            {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
            <button
              type="button"
              onClick={onApplePay}
              className={styles['btn-apple-pay']}
            />
          </>
        )}

        {supportsApplePay && !window.ApplePaySession && isDemoSite && (
          <div
            className={styles['fake-apple-pay']}
            onClick={onFakeApplePayClick}
          >
            <svg focusable="false" width={70} height={50} viewBox="0 0 60 40">
              <path
                // eslint-disable-next-line max-len
                d="M 15.355469 12.96875 C 14.816406 13.609375 13.949219 14.113281 13.085938 14.039062 C 12.976562 13.175781 13.398438 12.253906 13.894531 11.6875 C 14.4375 11.03125 15.382812 10.5625 16.148438 10.523438 C 16.238281 11.425781 15.886719 12.308594 15.355469 12.96875 M 16.140625 14.210938 C 14.886719 14.140625 13.816406 14.925781 13.21875 14.925781 C 12.617188 14.925781 11.703125 14.25 10.714844 14.265625 C 9.425781 14.285156 8.226562 15.015625 7.566406 16.175781 C 6.214844 18.503906 7.214844 21.949219 8.523438 23.839844 C 9.164062 24.777344 9.929688 25.804688 10.9375 25.769531 C 11.894531 25.734375 12.273438 25.148438 13.425781 25.148438 C 14.589844 25.148438 14.921875 25.769531 15.933594 25.75 C 16.980469 25.734375 17.636719 24.8125 18.277344 23.875 C 19.007812 22.8125 19.304688 21.777344 19.324219 21.722656 C 19.304688 21.703125 17.304688 20.9375 17.285156 18.628906 C 17.265625 16.699219 18.863281 15.78125 18.933594 15.726562 C 18.035156 14.390625 16.628906 14.25 16.140625 14.210938 M 23.378906 11.597656 L 23.378906 25.652344 L 25.5625 25.652344 L 25.5625 20.847656 L 28.582031 20.847656 C 31.339844 20.847656 33.277344 18.953125 33.277344 16.214844 C 33.277344 13.472656 31.375 11.597656 28.652344 11.597656 Z M 25.5625 13.4375 L 28.078125 13.4375 C 29.96875 13.4375 31.050781 14.445312 31.050781 16.222656 C 31.050781 18 29.96875 19.015625 28.066406 19.015625 L 25.5625 19.015625 Z M 37.265625 25.761719 C 38.632812 25.761719 39.90625 25.066406 40.480469 23.96875 L 40.527344 23.96875 L 40.527344 25.652344 L 42.546875 25.652344 L 42.546875 18.65625 C 42.546875 16.628906 40.925781 15.320312 38.425781 15.320312 C 36.109375 15.320312 34.398438 16.644531 34.332031 18.46875 L 36.300781 18.46875 C 36.460938 17.601562 37.265625 17.035156 38.363281 17.035156 C 39.699219 17.035156 40.445312 17.65625 40.445312 18.800781 L 40.445312 19.578125 L 37.722656 19.738281 C 35.191406 19.890625 33.820312 20.929688 33.820312 22.730469 C 33.820312 24.554688 35.234375 25.761719 37.265625 25.761719 Z M 37.851562 24.09375 C 36.6875 24.09375 35.949219 23.535156 35.949219 22.675781 C 35.949219 21.792969 36.660156 21.28125 38.019531 21.199219 L 40.445312 21.046875 L 40.445312 21.839844 C 40.445312 23.15625 39.328125 24.09375 37.851562 24.09375 Z M 45.242188 29.476562 C 47.371094 29.476562 48.371094 28.664062 49.246094 26.203125 L 53.078125 15.457031 L 50.859375 15.457031 L 48.289062 23.757812 L 48.246094 23.757812 L 45.675781 15.457031 L 43.394531 15.457031 L 47.089844 25.6875 L 46.890625 26.3125 C 46.558594 27.367188 46.019531 27.769531 45.054688 27.769531 C 44.882812 27.769531 44.546875 27.753906 44.414062 27.734375 L 44.414062 29.421875 C 44.539062 29.457031 45.082031 29.476562 45.242188 29.476562 Z M 45.242188 29.476562 "
                style={{
                  stroke: 'none',
                  fillRule: 'nonzero',
                  fill: 'rgb(255, 255, 255)',
                  fillOpacity: 1,
                }}
              />
            </svg>
          </div>
        )}

        {supportsGooglePay && (
          <GooglePayButton
            buttonType="plain"
            environment={
              process.env.REACT_APP_ENV === 'prod' ? 'PRODUCTION' : 'TEST'
            }
            className={styles['btn-google-pay-container']}
            buttonLocale="ja"
            paymentRequest={googlePaymentRequest}
            onCancel={() => onGooglePayCancel()}
            onError={() => onGooglePayCancel()}
            onClick={() => onGooglePayClick()}
            onLoadPaymentData={onGooglePay}
          />
        )}
      </nav>
      {showApplePayPopup && (
        <div className={styles['apple-pay-overlay']}>
          <div className={styles.popup}>
            <img src={ApplePayMark} alt="Apple Pay" height={64} />
            <h2>Apple Pay support</h2>
            <p>
              Your customer can now check out in seconds by simply
              authenticating the payment through Face ID or Touch ID.
            </p>
            <nav>
              <a
                href="https://docs.smartpay.co"
                rel="noopener noreferrer"
                target="_blank"
              >
                Documentation
              </a>
              <button type="button" onClick={() => setShowApplePayPopup(false)}>
                Return to demo
              </button>
            </nav>
          </div>
        </div>
      )}
    </>
  );
};

export const SelectMethodForm = ({
  methodSelectorInfoMessage,
  UnderMethodSelectorComponent,
  onTriggerMethodSelector,
  isTestAccount,
  errorMessage,
  submitButtonLabel,
  onPay,
}: {
  methodSelectorInfoMessage?: string;
  UnderMethodSelectorComponent?: React.ReactNode;
  onTriggerMethodSelector: Function;
  isTestAccount: boolean;
  errorMessage: string | undefined;
  submitButtonLabel: string;
  onPay: MouseEventHandler<HTMLButtonElement>;
}) => {
  const { isTestSession, isDemoSite } = useAppSelector((state) => state.misc);
  const { selectedPaymentMethod } = usePaymentMethods();

  return (
    <>
      <h4 className={screenStyles['card-form-title']}>
        <div className={screenStyles.label}>
          お支払い方法
          {isTestSession && (
            <Popup
              trigger={
                <img
                  src={IconInfoGray}
                  width={20}
                  height={20}
                  loading="lazy"
                  alt="Tip"
                />
              }
              content="テスト決済では、以下のカードを用意しており、これらのカードのみ使用可能です。"
              hasMarginLeft
              position="top-right"
              shouldDisplayOnInit={isDemoSite}
            />
          )}
        </div>
      </h4>

      <MethodSelector
        onTriggerMethodSelector={onTriggerMethodSelector}
        isTestAccount={isTestAccount}
        infoMessage={methodSelectorInfoMessage}
      />

      {UnderMethodSelectorComponent}

      {errorMessage && (
        <div className={styles['error-wrapper']}>
          <p id="error_message">{errorMessage}</p>
        </div>
      )}

      <Button
        id="btn_submit_payment"
        label={submitButtonLabel}
        className={screenStyles['btn-pay']}
        onClick={onPay}
        disabled={!selectedPaymentMethod}
      />
    </>
  );
};

const PromotionCodeForm = () => {
  const dispatch = useAppDispatch();
  const sessionIdWithSignature = useAppSelector(
    (state) => state.misc.sessionIdWithSignature
  );
  const promotionCode = useAppSelector((state) => state.misc.promotionCode);
  const promotion = useAppSelector((state) => state.misc.promotion);
  const goods = useAppSelector((state) => state.good.data);
  const discountAmount = useAppSelector(
    (state) => state.paymentMethod.discountAmount
  );
  const hasAppliedPromotionCode = useAppSelector(
    (state) => state.good.hasAppliedPromotionCode
  );
  const [formPromotionCode, setFormPromotionCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [
    shouldDisplayPromotionCodeNotice,
    setShouldDisplayPromotionCodeNotice,
  ] = useState(false);
  const isDemoSite = useAppSelector((state) => state.misc.isDemoSite);
  const [isReservingDiscount, setIsReservingDiscount] = useState(false);

  const onApplyPromotionCode = createEventHandler(async () => {
    setIsReservingDiscount(true);

    if (formPromotionCode && sessionIdWithSignature) {
      const getPromotionCodeResultAction = await dispatch(
        getPromotionCode({
          sessionIdWithSignature,
          promotionCode: formPromotionCode,
        })
      );

      if (getPromotionCode.fulfilled.match(getPromotionCodeResultAction)) {
        const { payload } = getPromotionCodeResultAction;
        const { isValid, errorCode } = validatePromotion(
          payload,
          goods?.amount || 0
        );

        if (isValid) {
          const reserveDiscountResultAction = await dispatch(
            reserveDiscount({
              promotionCode: formPromotionCode,
              orderID: goods.id || '',
            })
          );

          if (reserveDiscount.fulfilled.match(reserveDiscountResultAction)) {
            dispatch(setHasAppliedPromotionCode(true));
          } else {
            setErrorMessage(
              ERROR_MESSAGES.PAYMENT[
                (reserveDiscountResultAction.payload as APIPayload)?.errorCode
              ] || ERROR_MESSAGES.SHARED['wrong-promotion-code']
            );
          }
        } else {
          setErrorMessage(
            ERROR_MESSAGES.PAYMENT[errorCode] ||
              ERROR_MESSAGES.SHARED['wrong-promotion-code']
          );
        }
      } else {
        setErrorMessage(ERROR_MESSAGES.SHARED['wrong-promotion-code']);
      }
    }

    setIsReservingDiscount(false);
  });

  const onInputKeyDown: KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (event.key === 'Enter') {
      onApplyPromotionCode();
    }
  };

  const shouldDisplayApplyButton =
    formPromotionCode && !errorMessage && !hasAppliedPromotionCode;

  return !promotionCode ? (
    <div className={styles['promotion-form']}>
      <h4 className={screenStyles['card-form-title']}>
        プロモーションコード
        {isDemoSite && (
          <Popup
            trigger={
              <img
                src={IconInfoGray}
                width={20}
                height={20}
                loading="lazy"
                alt="Tip"
              />
            }
            content="SPRINGSALE2022をお試しください。"
            hasMarginLeft
            position="top-right"
            shouldDisplayOnInit={isDemoSite}
          />
        )}
      </h4>
      <div className={styles.form}>
        <TextInput
          name="promotionCode"
          type="promotion-code"
          value={formPromotionCode}
          aria-label="コード"
          placeholder="コードを入力"
          updatePairs={(pairs: { promotionCode: string }) => {
            setFormPromotionCode(pairs.promotionCode);
            setErrorMessage('');
            setShouldDisplayPromotionCodeNotice(true);
          }}
          disabled={hasAppliedPromotionCode}
          onKeyDown={onInputKeyDown}
          className={styles.input}
          onBlur={onApplyPromotionCode}
          {...(shouldDisplayApplyButton && {
            inputStyles: { paddingRight: 80 },
          })}
        />
        {shouldDisplayApplyButton && (
          <Button
            label="適用する"
            id="btn_submit"
            type="submit"
            className={styles['btn-apply']}
            onClick={onApplyPromotionCode}
            loading={isReservingDiscount}
          />
        )}
      </div>
      {shouldDisplayPromotionCodeNotice && (
        <p className={styles['promotion-code-notice']}>
          注意：クーポンコードは入力後5分間のみ有効です。
        </p>
      )}
      {hasAppliedPromotionCode && promotion && (
        <div className={styles['info-wrapper']}>
          <p>
            <span className="price-amount">
              {currencyFormatter(discountAmount, { dollarSign: false })}
            </span>
            円引きになります。
          </p>
        </div>
      )}
      {errorMessage && (
        <div className={styles['error-wrapper']}>
          <p>{errorMessage}</p>
        </div>
      )}
    </div>
  ) : null;
};

const PayActionForm = ({
  errorMessage,
  onPay,
  onApplePay,
  onGooglePay,
  onGooglePayCancel,
  onTriggerMethodSelector,
  onSubmitStart,

  totalAmount,
  googlePaymentRequest,
}: {
  errorMessage: string | undefined;
  onPay: MouseEventHandler<HTMLButtonElement>;
  onApplePay: MouseEventHandler<HTMLButtonElement>;
  onGooglePay: (paymentData: google.payments.api.PaymentData) => void;
  onGooglePayCancel: Function;
  onTriggerMethodSelector: Function;
  onSubmitStart: Function;
  totalAmount: number;
  googlePaymentRequest: google.payments.api.PaymentDataRequest;
}) => {
  useTrackPageView();

  const supportsApplePay = useAppSelector(
    (state) => state.misc.supportsApplePay
  );
  const supportsGooglePay = useAppSelector(
    (state) => state.misc.supportsGooglePay
  );
  const isTestAccount = useAppSelector((state) => state.auth.isTestAccount);
  const canUsePromotionCodes = useAppSelector(
    (state) => state.misc.canUsePromotionCodes
  );

  const submitButtonLabel = `今は ${currencyFormatter(
    getAmountForTheFirst(totalAmount)
  )} 支払う`;

  return (
    <section className={styles.action}>
      {canUsePromotionCodes && <PromotionCodeForm />}
      <SelectMethodForm
        isTestAccount={isTestAccount}
        onTriggerMethodSelector={onTriggerMethodSelector}
        errorMessage={errorMessage}
        submitButtonLabel={submitButtonLabel}
        onPay={onPay}
      />
      <p className={cx(styles.secure)}>支払いは安全で暗号化されています</p>
      {!!window.PaymentRequest && (supportsApplePay || supportsGooglePay) && (
        <>
          <div className={styles.or} data-text="または" />
          <ThirdPartyButtons
            onApplePay={onApplePay}
            onGooglePay={onGooglePay}
            onGooglePayCancel={onGooglePayCancel}
            onGooglePayClick={onSubmitStart}
            googlePaymentRequest={googlePaymentRequest}
          />
        </>
      )}
    </section>
  );
};

export default PayActionForm;
